import React from "react"
import { Collapsible } from "@base-ui/react/collapsible"
import { Popover } from "@base-ui/react/popover"
import { Button } from "../button"
import { Icon } from "../icon"
import { VisuallyHidden } from "../visually-hidden"
import { ProgressCircle } from "../progress-circle"
import useLocalStorage from "../use-localstorage"
import { popupTransition } from "../utils/popup-transition"

// =============================================================================
// Props
// =============================================================================

type TrialData = {
  /** Days remaining in the trial */
  daysRemaining: number
  /** Total days in the trial period */
  daysTotal: number
  /** Called when "Upgrade now" is clicked */
  onUpgrade: () => void
}

export type TrialIndicatorProps = TrialData & {
  /**
   * Initial open state used only when no preference has been stored yet.
   * Defaults to true. Once the user expands or collapses the card, that choice
   * is persisted and takes precedence on subsequent mounts.
   */
  defaultOpen?: boolean
}

export type TrialIndicatorPillProps = TrialData

// =============================================================================
// Styles
// =============================================================================

const cardStyles = [
  "flex flex-col rounded-lg p-2",
  "bg-blue-100 text-gray-900"
].join(" ")

const triggerStyles = [
  // 'group' enables group-aria-[expanded] variants on children
  "group flex w-full items-center gap-2 p-2",
  "rounded-lg"
].join(" ")

const panelStyles = [
  "h-[var(--collapsible-panel-height)] overflow-hidden",
  "transition-[height,opacity] ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 duration-150"
].join(" ")

const collapsedSummaryStyles = [
  "text-sm font-semibold leading-6",
  "transition-opacity duration-150 ease-out",
  "group-aria-[expanded=true]:opacity-0 group-aria-[expanded=true]:pointer-events-none"
].join(" ")

const pillStyles = [
  "inline-flex items-center rounded-lg p-2",
  "bg-blue-100 text-blue-800",
  // Inset ring so the focus indicator isn't clipped by the callout's overflow-hidden.
  "ring-2 ring-inset ring-transparent focus-visible:ring-gray-900 outline-none"
].join(" ")

// The hover card is portalled out of the callout, so it can't inherit the
// nav's width. It carries its own fixed width via inline style (this project's
// Tailwind scale has no `w-60`/`w-64` step), matching the expanded card width.
const CARD_WIDTH = 240

const hoverCardStyles = [
  "rounded-lg p-2",
  "bg-blue-100 text-gray-900 shadow-lg ring-1 ring-gray-900/5",
  popupTransition()
].join(" ")

// =============================================================================
// Shared content
// =============================================================================

function useTrialProgress(daysRemaining: number, daysTotal: number) {
  const safeDaysRemaining = Math.max(0, daysRemaining)
  const progress =
    daysTotal > 0 ? Math.min(safeDaysRemaining / daysTotal, 1) : 0
  const dayLabel = safeDaysRemaining === 1 ? "day" : "days"
  return { safeDaysRemaining, progress, dayLabel }
}

function TrialRing({ progress }: { progress: number }) {
  return (
    <span className="text-blue-800 flex shrink-0">
      <ProgressCircle progress={progress} />
    </span>
  )
}

type TrialBodyProps = {
  daysRemaining: number
  dayLabel: string
  onUpgrade: () => void
}

/**
 * The trial copy and upgrade CTA shared between the card's details panel and
 * the pill's hover card, so the two stay visually identical.
 */
function TrialBody({ daysRemaining, dayLabel, onUpgrade }: TrialBodyProps) {
  return (
    <React.Fragment>
      <div className="w-full flex flex-col gap-1 text-sm leading-6">
        <p className="font-semibold">
          {daysRemaining} {dayLabel} left on your free trial
        </p>
        <p>
          Upgrade to continue to use all the features you have access to without
          interruption.
        </p>
      </div>
      <Button variant="brand" size="small" onClick={onUpgrade}>
        Upgrade now
      </Button>
    </React.Fragment>
  )
}

// =============================================================================
// Components
// =============================================================================

/**
 * Collapsible card in the navigation sidebar showing trial days remaining
 * and an upgrade CTA. Click the header row to expand or collapse.
 *
 * For the collapsed sidebar, pair this with `TrialIndicatorPill` via
 * `Navigation.FooterCallout`'s `collapsed` slot.
 */
export function TrialIndicator({
  daysRemaining,
  daysTotal,
  onUpgrade,
  defaultOpen = true
}: TrialIndicatorProps) {
  const { safeDaysRemaining, progress, dayLabel } = useTrialProgress(
    daysRemaining,
    daysTotal
  )
  const [open, setOpen] = useLocalStorage("trial-indicator:open", defaultOpen)

  return (
    <div data-slot="trial-indicator" className={cardStyles}>
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Collapsible.Trigger type="button" className={triggerStyles}>
          <TrialRing progress={progress} />
          <div className="flex-1 text-left">
            <span aria-hidden className={collapsedSummaryStyles}>
              {safeDaysRemaining} / {daysTotal} {dayLabel}
            </span>
            <VisuallyHidden>
              {safeDaysRemaining} / {daysTotal} {dayLabel} remaining in trial
            </VisuallyHidden>
          </div>
          <Icon
            name="down"
            className="shrink-0 transition-transform group-aria-[expanded=false]:-rotate-180"
            aria-hidden
          />
        </Collapsible.Trigger>
        <Collapsible.Panel className={panelStyles}>
          <div className="flex flex-col gap-4 p-2 items-start">
            <TrialBody
              daysRemaining={safeDaysRemaining}
              dayLabel={dayLabel}
              onUpgrade={onUpgrade}
            />
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  )
}

TrialIndicator.displayName = "TrialIndicator"

/**
 * Compact trial indicator (progress ring only) that reveals the full card —
 * including the interactive "Upgrade now" button — in a popover. The popover
 * opens on hover and can also be opened from the keyboard by activating the
 * trigger, so the content and CTA are reachable by assistive technology.
 * For use in the collapsed sidebar via `Navigation.FooterCallout`.
 */
export function TrialIndicatorPill({
  daysRemaining,
  daysTotal,
  onUpgrade
}: TrialIndicatorPillProps) {
  const { safeDaysRemaining, progress, dayLabel } = useTrialProgress(
    daysRemaining,
    daysTotal
  )

  return (
    <Popover.Root>
      <Popover.Trigger
        openOnHover
        delay={200}
        className={pillStyles}
        render={
          <button
            type="button"
            aria-label={`${safeDaysRemaining} / ${daysTotal} ${dayLabel} remaining in trial`}
          />
        }
      >
        <TrialRing progress={progress} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner
          side="right"
          sideOffset={12}
          className="isolate z-[5002]"
        >
          <Popover.Popup
            data-slot="trial-indicator-hover-card"
            className={hoverCardStyles}
            style={{ width: CARD_WIDTH }}
          >
            <div className="flex flex-col gap-4 p-2 items-start">
              <TrialRing progress={progress} />
              <TrialBody
                daysRemaining={safeDaysRemaining}
                dayLabel={dayLabel}
                onUpgrade={onUpgrade}
              />
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

TrialIndicatorPill.displayName = "TrialIndicatorPill"
